import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { TermsModal } from '../components/TermsModal';
import { InteractiveBackground } from '../components/InteractiveBackground';

export const Route = createFileRoute('/register-client-invite/$token')({
  component: RegisterClientInvitePage,
});

const TERMS_VERSION = '1.0.0';

const inputStyle = {
  background: '#F8F8F8',
  border: 'none',
  borderRadius: '20px',
  fontFamily: "'Poppins', sans-serif",
  fontSize: '12px',
  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
  color: '#2D3748',
};

const labelStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: '12px',
  color: '#2D3748',
  fontWeight: 500,
};

function RegisterClientInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [form, setForm] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
  });
  const [error, setError] = useState('');
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsType, setTermsType] = useState<'terms' | 'privacy'>('terms');
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const registerMutation = useMutation({
    mutationFn: async (payload: {
      token: string;
      password: string;
      fullName: string;
      termsAccepted: boolean;
      termsVersion: string;
    }) => {
      const response = await api.post('/auth/register-client-invite', payload);
      return response.data.data;
    },
    onSuccess: (data) => {
      const { user, accessToken, refreshToken, csrfToken } = data;
      setAuth(user, accessToken, refreshToken, csrfToken);
      navigate({ to: '/client' });
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao criar conta');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.fullName.trim()) {
      setError('Informe seu nome');
      return;
    }
    if (form.password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    if (!form.termsAccepted) {
      setError('É necessário aceitar os termos de uso e política de privacidade');
      return;
    }

    registerMutation.mutate({
      token,
      password: form.password,
      fullName: form.fullName.trim(),
      termsAccepted: form.termsAccepted,
      termsVersion: TERMS_VERSION,
    });
  };

  const openTermsModal = (type: 'terms' | 'privacy') => {
    setTermsType(type);
    if (type === 'terms') setTermsModalOpen(true);
    else setPrivacyModalOpen(true);
  };

  return (
    <>
      <div className="min-h-screen relative overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <InteractiveBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10 p-6">
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
              maxWidth: '520px',
              width: '100%',
            }}
          >
            <h1 style={{ fontSize: '18px', fontWeight: 600, color: '#06141B' }}>
              Criar acesso ao Portal do Cliente
            </h1>
            <p className="mt-1" style={{ fontSize: '12px', color: '#718096' }}>
              Defina sua senha para acessar os processos compartilhados com você.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label style={labelStyle}>Nome completo</label>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
                  placeholder="Ex: Maria Silva"
                  className="w-full px-4 py-3 mt-2"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Senha</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-4 py-3 mt-2"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Confirmar senha</label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm((s) => ({ ...s, confirmPassword: e.target.value }))}
                  placeholder="Repita a senha"
                  className="w-full px-4 py-3 mt-2"
                  style={inputStyle}
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.termsAccepted}
                  onChange={(e) => setForm((s) => ({ ...s, termsAccepted: e.target.checked }))}
                  className="mt-1"
                />
                <div className="text-[12px]" style={{ color: '#2D3748' }}>
                  Eu li e aceito os{' '}
                  <button
                    type="button"
                    onClick={() => openTermsModal('terms')}
                    className="underline"
                    style={{ color: '#06141B' }}
                  >
                    termos de uso
                  </button>{' '}
                  e a{' '}
                  <button
                    type="button"
                    onClick={() => openTermsModal('privacy')}
                    className="underline"
                    style={{ color: '#06141B' }}
                  >
                    política de privacidade
                  </button>
                  .
                </div>
              </div>

              {error && (
                <div ref={errorRef} className="p-3 rounded-[12px]" style={{ background: '#FEF2F2', color: '#B91C1C' }}>
                  <p style={{ fontSize: '12px' }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={registerMutation.isPending}
                className="w-full py-3 rounded-[12px] font-semibold transition-opacity disabled:opacity-50"
                style={{ background: '#06141B', color: 'white', fontSize: '12px' }}
              >
                {registerMutation.isPending ? 'Criando...' : 'Criar conta'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <TermsModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        type="terms"
      />
      <TermsModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        type="privacy"
      />
    </>
  );
}

